import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionIcon } from "@mantine/core";
import { MdOutlineSync, MdRecordVoiceOver } from "react-icons/md";
import { getSpeechApi } from "../../api";
import type { SpeechConfig } from "../types";
import { useSettings } from "../../SettingsContext";

export interface TextToSpeechProps {
    speechConfig: SpeechConfig;
    response: string;
    index?: number;
}

export const TextToSpeech = ({ speechConfig, response, index }: TextToSpeechProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [localPlayingState, setLocalPlayingState] = useState(false);
    const { t } = useTranslation();
    const [settings] = useSettings();

    const playAudio = async (url: string) => {
        speechConfig.audio.src = url;
        await speechConfig.audio
            .play()
            .then(() => {
                speechConfig.audio.onended = () => {
                    speechConfig.setIsPlaying(false);
                    setLocalPlayingState(false);
                };
                speechConfig.setIsPlaying(true);
                setLocalPlayingState(true);
            })
            .catch(() => {
                alert("Failed to play speech output.");
                console.error("Failed to play speech output.");
                speechConfig.setIsPlaying(false);
                setLocalPlayingState(false);
            });
    };

    const startOrStopSpeech = async () => {
        const { isPlaying, audio, setIsPlaying, speechUrls } = speechConfig;
        if (isPlaying) {
            audio.pause();
            audio.currentTime = 0;
            setIsPlaying(false);
            setLocalPlayingState(false);
            return;
        }
        if (index && !!speechUrls[index]) {
            const speechUrl = speechUrls[index];
            if (speechUrl) {
                playAudio(speechUrl);
            }
            return;
        }
        setIsLoading(true);
        await getSpeechApi(response, settings.speechRate).then(async speechUrl => {
            if (!speechUrl) {
                alert("Speech output is not available.");
                console.error("Speech output is not available.");
                return;
            }
            setIsLoading(false);
            speechConfig.setSpeechUrls(speechConfig.speechUrls.map((url, i) => (i === index ? speechUrl : url)));
            playAudio(speechUrl);
        });
    };

    const color = localPlayingState ? "red.5" : "blue.9";
    return isLoading ? (
        <ActionIcon variant="transparent" color="blue.9" disabled={true}>
            <MdOutlineSync size={20} />
        </ActionIcon>
    ) : (
        <ActionIcon variant="transparent" color={color} onClick={startOrStopSpeech}>
            <MdRecordVoiceOver size={20} />
        </ActionIcon>
    );
};
